/**
 * «Відомості про ОЗ» (рішення власника 2026-08-27, як довідка 1С «Сведения
 * об ОС»): повна картка по всіх активах організації або по одному, з друком.
 * Фільтри: організація / вид / статус / актив. Друк — .print-page + print-mode.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import type { AssetKind } from '@/api/assets';
import { ASSET_KIND_UA, ASSET_STATUS_UA, assetsRegistry, type AssetInfoItem } from '@/api/assetsRegistry';
import { useDefaults } from '@/store/defaults';
import SearchSelect from '@/components/SearchSelect';

const fmt2 = (v: string | number | null | undefined) => v == null || v === '' ? ''
  : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtD = (v: string | null | undefined) => v ? new Date(v).toLocaleDateString('uk-UA') : '';
const selStyle = { width: 170, flex: '0 0 auto' } as const;

export default function AssetsInfoPage() {
  const [sp] = useSearchParams();
  const defaultOrgId = useDefaults((s) => s.defaultOrgId);
  const [orgId, setOrgId] = useState(sp.get('organization_id') || defaultOrgId || '');
  const [kind, setKind] = useState<AssetKind | ''>((sp.get('kind') as AssetKind) || '');
  const [status, setStatus] = useState('');
  const [assetId, setAssetId] = useState(sp.get('asset_id') || '');
  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const listQ = useQuery({
    queryKey: ['assets-registry-all', orgId, kind],
    queryFn: () => assetsRegistry.list({ organization_id: orgId, kind: kind || undefined, limit: 500 }),
    enabled: !!orgId,
  });
  const infoQ = useQuery({
    queryKey: ['assets-info', orgId, kind, status, assetId],
    queryFn: () => assetsRegistry.info({
      organization_id: orgId, kind: kind || undefined, status: status || undefined,
      asset_id: assetId || undefined,
    }),
    enabled: !!orgId,
  });
  const items = infoQ.data ?? [];
  const orgName = useMemo(() => orgList.data?.items.find((o) => o.id === orgId)?.name ?? '', [orgList.data, orgId]);

  return (
    <div className="card">
      <div className="toolbar print-hide" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Відомості про необоротні активи</h2>
        <button className="primary" style={{ marginLeft: 'auto' }} onClick={() => window.print()}>друкувати</button>
      </div>
      {/* 2026-08-29 (рішення власника): актив — в одному рядку з видом, поле з пошуком. */}
      <div className="print-hide" style={{ display: 'flex', gap: '0.4rem', flexWrap: 'nowrap', alignItems: 'center', marginBottom: '1rem' }}>
        <select value={orgId} onChange={(e) => { setOrgId(e.target.value); setAssetId(''); }} style={selStyle}>
          <option value="">— організація —</option>
          {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
        </select>
        <select value={kind} onChange={(e) => { setKind(e.target.value as AssetKind | ''); setAssetId(''); }} style={selStyle}>
          <option value="">Усі види</option>
          <option value="FIXED_ASSET">Основні засоби (ОЗ)</option>
          <option value="INTANGIBLE">Нематеріальні активи (НМА)</option>
          <option value="LOW_VALUE">МШП / МНМА</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} style={selStyle}>
          <option value="">Усі статуси</option>
          {Object.entries(ASSET_STATUS_UA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <span style={{ flex: 1, minWidth: 260, display: 'flex', alignItems: 'center', gap: 2 }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <SearchSelect
              items={(listQ.data?.items ?? []).map((a) => ({ id: a.id, label: a.name, hint: a.inventory_number }))}
              value={assetId} onChange={setAssetId} placeholder="актив: усі (пошук за назвою / інв. №)" />
          </span>
          {assetId && <button type="button" style={{ padding: '0 0.4rem' }} onClick={() => setAssetId('')} title="Усі активи">×</button>}
        </span>
      </div>

      <div className="print-page" style={{ fontSize: '0.82rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <b>Відомості про необоротні активи{orgName ? ` — ${orgName}` : ''}</b>
          <span>Дата відомостей: {new Date().toLocaleDateString('uk-UA')}</span>
        </div>
        {!orgId && <p style={{ color: 'var(--muted)' }}>Оберіть організацію.</p>}
        {orgId && infoQ.isLoading && <p>Завантаження…</p>}
        {orgId && !infoQ.isLoading && items.length === 0 && <p style={{ color: 'var(--muted)' }}>Активів немає.</p>}
        {items.map((a) => <AssetInfoBlock key={a.id} a={a} />)}
      </div>
    </div>
  );
}

function Row({ l, v, l2, v2 }: { l: string; v?: React.ReactNode; l2?: string; v2?: React.ReactNode }) {
  return (
    <tr>
      <td style={{ fontWeight: 600, width: '26%', padding: '1px 6px' }}>{l}</td>
      <td style={{ padding: '1px 6px', width: l2 ? '24%' : undefined }} colSpan={l2 ? 1 : 3}>{v ?? ''}</td>
      {l2 && <td style={{ fontWeight: 600, width: '26%', padding: '1px 6px' }}>{l2}</td>}
      {l2 && <td style={{ padding: '1px 6px' }}>{v2 ?? ''}</td>}
    </tr>
  );
}
function Sect({ title, color }: { title: string; color: string }) {
  return <tr><td colSpan={4} style={{ background: color, fontWeight: 700, padding: '1px 6px' }}>{title}</td></tr>;
}

function AssetInfoBlock({ a }: { a: AssetInfoItem }) {
  const isLv = a.kind === 'LOW_VALUE';
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #999', marginBottom: 14, pageBreakInside: 'avoid' }}>
      <tbody>
        <tr style={{ background: '#f5e6cf' }}>
          <td style={{ fontWeight: 700, fontSize: '0.95rem', padding: '2px 6px' }}>Найменування:</td>
          <td style={{ fontSize: '0.95rem', padding: '2px 6px' }} colSpan={2}>{a.name}</td>
          <td style={{ fontWeight: 700, padding: '2px 6px' }}>Код: {a.inventory_number}</td>
        </tr>
        <Row l="Вид активу" v={ASSET_KIND_UA[a.kind]} l2="Група" v2={a.group_name ?? ''} />
        <Row l="Коментар" v={a.comment ?? ''} />
        <Sect title="Бухгалтерський облік" color="#bff0e8" />
        <Sect title="Місцезнаходження" color="#f7c6f0" />
        <Row l="Організація" v={a.organization_name ?? ''} />
        <Row l="Інвентарний номер" v={a.inventory_number} l2="Статус" v2={ASSET_STATUS_UA[a.status] ?? a.status} />
        <Row l="Підрозділ" v={a.department_name ?? ''} l2="МВО" v2={a.responsible_name ?? ''} />
        <Row l="Введено в експлуатацію" v={fmtD(a.commissioning_date)} l2="Знято з обліку" v2={fmtD(a.retirement_date)} />
        <Row l="Документ введення" v={a.commissioning_doc ?? ''} />
        <Row l="Документ зняття" v={a.retirement_doc ?? ''} />
        <Sect title="Параметри" color="#f7c6f0" />
        <Row l="Рахунок обліку" v={a.account_code ?? ''} l2="Рахунок витрат" v2={a.expense_account_code ?? ''} />
        {!isLv ? (
          <>
            <Row l="Первісна вартість" v={fmt2(a.primary_cost)} l2="Ліквідаційна вартість" v2={fmt2(a.liquidation_value)} />
            <Row l="Накопичений знос" v={fmt2(a.accumulated_depreciation)} l2="Залишкова вартість" v2={fmt2(a.residual_value)} />
            <Row l="Метод нарахування амортизації" v={a.method_name ?? ''} l2="Нараховано в системі" v2={fmt2(a.depreciation_in_system)} />
            <Row l="Строк корисного використання" v={a.useful_life_months != null ? `${a.useful_life_months} міс.` : ''}
              l2="Періодів амортизовано" v2={a.elapsed_depreciation_periods ?? ''} />
          </>
        ) : (
          <>
            <Row l="Первісна вартість" v={fmt2(a.primary_cost)} l2="Списано вартості" v2={fmt2(a.accumulated_depreciation)} />
            <Row l="Метод списання" v={a.write_off_method === 'AT_USE_100' ? '100% при введенні' : a.write_off_method === 'SPLIT_50_50' ? '50/50' : ''}
              l2="Кількість" v2={a.quantity ?? ''} />
          </>
        )}
      </tbody>
    </table>
  );
}
